import { useParams, Link } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { categories } from '@/mocks/categories';
import { games, type GameItem } from '@/mocks/games';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/base/Breadcrumb';
import SEOHead from '@/components/base/SEOHead';

const categoryImages: Record<string, string> = {
  rpg: 'https://readdy.ai/api/search-image?query=Epic%20fantasy%20role%20playing%20game%20artwork%20with%20warm%20golden%20lighting%2C%20adventure%20theme%2C%20detailed%20illustration%20style%2C%20orange%20and%20amber%20tones%2C%20cinematic%20composition%20on%20dark%20background&width=800&height=400&seq=cat-rpg-hero&orientation=landscape',
  'visual-novel': 'https://readdy.ai/api/search-image?query=Visual%20novel%20game%20artwork%20with%20romantic%20anime%20characters%20and%20soft%20warm%20lighting%2C%20storytelling%20theme%2C%20beautiful%20illustration%20style%2C%20soft%20orange%20and%20pink%20tones%20on%20gentle%20background&width=800&height=400&seq=cat-vn-hero&orientation=landscape',
  simulation: 'https://readdy.ai/api/search-image?query=Strategy%20simulation%20game%20artwork%20with%20kingdom%20building%20and%20management%20theme%2C%20warm%20bronze%20lighting%2C%20detailed%20illustration%20style%2C%20golden%20amber%20tones%20on%20dark%20background&width=800&height=400&seq=cat-sim-hero&orientation=landscape',
  action: 'https://readdy.ai/api/search-image?query=Action%20adventure%20game%20artwork%20with%20dynamic%20combat%20scene%2C%20warm%20dramatic%20lighting%2C%20intense%20illustration%20style%2C%20fiery%20orange%20and%20amber%20tones%20on%20dark%20background&width=800&height=400&seq=cat-action-hero&orientation=landscape',
  puzzle: 'https://readdy.ai/api/search-image?query=Mystery%20puzzle%20escape%20room%20game%20artwork%20with%20intriguing%20atmosphere%2C%20warm%20enigmatic%20lighting%2C%20detailed%20illustration%20style%2C%20soft%20orange%20and%20brown%20tones%20on%20dark%20background&width=800&height=400&seq=cat-puzzle-hero&orientation=landscape',
  mobile: 'https://readdy.ai/api/search-image?query=Mobile%20gaming%20illustration%20with%20smartphone%20and%20game%20characters%2C%20warm%20modern%20lighting%2C%20clean%20design%20style%2C%20soft%20orange%20and%20peach%20tones%20on%20clean%20background&width=800&height=400&seq=cat-mobile-hero&orientation=landscape',
};

const gameCardImages: Record<string, string> = {
  'game-shadow-legend': 'https://readdy.ai/api/search-image?query=Dark%20fantasy%20anime%20game%20card%20art%20with%20warrior%20character%2C%20warm%20torch%20lighting%2C%20mysterious%20atmosphere%2C%20orange%20and%20amber%20tones%2C%20professional%20illustration%20on%20dark%20background&width=600&height=400&seq=card-shadow-legend&orientation=landscape',
  'game-sakura-promise': 'https://readdy.ai/api/search-image?query=Romantic%20anime%20school%20game%20card%20art%20with%20cherry%20blossoms%2C%20warm%20sunset%20lighting%2C%20beautiful%20character%2C%20orange%20and%20pink%20tones%20on%20gentle%20background&width=600&height=400&seq=card-sakura-promise&orientation=landscape',
  'game-kingdom-warfare': 'https://readdy.ai/api/search-image?query=Epic%20fantasy%20kingdom%20game%20card%20art%20with%20castle%2C%20warm%20golden%20lighting%2C%20strategy%20theme%2C%20amber%20and%20bronze%20tones%20on%20dark%20background&width=600&height=400&seq=card-kingdom-warfare&orientation=landscape',
  'game-abyss-hunter': 'https://readdy.ai/api/search-image?query=Action%20game%20card%20art%20with%20hunter%20character%20and%20monsters%2C%20warm%20dramatic%20lighting%2C%20intense%20combat%2C%20orange%20and%20red%20tones%20on%20dark%20background&width=600&height=400&seq=card-abyss-hunter&orientation=landscape',
  'game-escape-room': 'https://readdy.ai/api/search-image?query=Mystery%20escape%20room%20game%20card%20art%20with%20puzzles%2C%20warm%20enigmatic%20lighting%2C%20intriguing%20atmosphere%2C%20orange%20and%20brown%20tones%20on%20dark%20background&width=600&height=400&seq=card-escape-room&orientation=landscape',
  'game-mobile-girlfriend': 'https://readdy.ai/api/search-image?query=Cute%20mobile%20dating%20game%20card%20art%20with%20anime%20girl%2C%20warm%20soft%20lighting%2C%20romantic%20atmosphere%2C%20orange%20and%20peach%20tones%20on%20clean%20background&width=600&height=400&seq=card-mobile-girlfriend&orientation=landscape',
  'game-dragon-vein': 'https://readdy.ai/api/search-image?query=Eastern%20fantasy%20game%20card%20art%20with%20dragon%20and%20warrior%2C%20warm%20mystical%20lighting%2C%20xianxia%20theme%2C%20orange%20and%20gold%20tones%20on%20dark%20background&width=600&height=400&seq=card-dragon-vein&orientation=landscape',
  'game-starlight-stage': 'https://readdy.ai/api/search-image?query=Idol%20simulation%20game%20card%20art%20with%20stage%20and%20spotlights%2C%20warm%20glamorous%20lighting%2C%20entertainment%20theme%2C%20orange%20and%20warm%20tones%20on%20clean%20background&width=600&height=400&seq=card-starlight-stage&orientation=landscape',
  'game-neon-city': 'https://readdy.ai/api/search-image?query=Cyberpunk%20visual%20novel%20game%20card%20art%20with%20neon%20cityscape%2C%20warm%20amber%20neon%20lights%2C%20futuristic%20noir%20theme%2C%20orange%20tones%20on%20dark%20background&width=600&height=400&seq=card-neon-city&orientation=landscape',
  'game-relic-explorer': 'https://readdy.ai/api/search-image?query=Adventure%20exploration%20game%20card%20art%20with%20ancient%20ruins%2C%20warm%20mysterious%20lighting%2C%20tomb%20raider%20theme%2C%20orange%20and%20amber%20tones%20on%20dark%20background&width=600&height=400&seq=card-relic-explorer&orientation=landscape',
  'game-wheel-fate': 'https://readdy.ai/api/search-image?query=Time%20travel%20fantasy%20game%20card%20art%20with%20clock%20and%20portals%2C%20warm%20magical%20lighting%2C%20mystical%20theme%2C%20orange%20and%20gold%20tones%20on%20dark%20background&width=600&height=400&seq=card-wheel-fate&orientation=landscape',
  'game-sweet-bakery': 'https://readdy.ai/api/search-image?query=Cute%20baking%20dating%20game%20card%20art%20with%20desserts%20and%20anime%20girl%2C%20warm%20cozy%20lighting%2C%20sweet%20theme%2C%20orange%20and%20peach%20tones%20on%20clean%20background&width=600&height=400&seq=card-sweet-bakery&orientation=landscape',
};

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const [activeTab, setActiveTab] = useState(slug || 'rpg');
  const [filteredGames, setFilteredGames] = useState<GameItem[]>([]);

  const currentCategory = categories.find((c) => c.slug === activeTab);

  useEffect(() => {
    if (slug) {
      setActiveTab(slug);
    }
  }, [slug]);

  useEffect(() => {
    setFilteredGames(games.filter((g) => g.category === activeTab));
  }, [activeTab]);

  const categoryName = currentCategory?.name || '游戏分类';
  const categoryDesc = currentCategory?.description || '';

  const categorySchema = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: `${categoryName} - 黄游下载`,
    description: categoryDesc,
    url: `https://huangyou-download.com/category/${activeTab}`,
    mainEntity: {
      '@type': 'ItemList',
      itemListElement: filteredGames.map((game, i) => ({
        '@type': 'ListItem',
        position: i + 1,
        item: {
          '@type': 'VideoGame',
          name: game.title,
          description: game.description,
          url: `https://huangyou-download.com/category/${game.category}`,
        },
      })),
    },
  };

  return (
    <>
      <SEOHead
        title={`${categoryName} - 黄游下载资源`}
        description={`${categoryDesc}。黄游下载${categoryName}专属频道，安全快速下载，精选高质量${categoryName}成人游戏资源。`}
        keywords={`${categoryName},黄游下载,成人游戏下载,${categoryName}游戏,黄游资源下载`}
        canonicalUrl={`/category/${activeTab}`}
      />

      <div className="min-h-screen bg-background-50">
        <Navbar />

        <main>
          {/* Category Hero */}
          <section className="relative w-full h-[280px] md:h-[360px] flex items-center justify-center overflow-hidden">
            <img
              src={categoryImages[activeTab] || categoryImages.rpg}
              alt={`${categoryName} - 黄游下载`}
              className="absolute inset-0 w-full h-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-black/35 via-black/20 to-black/40"></div>
            <div className="relative z-10 w-full max-w-4xl mx-auto px-4 text-center pt-16">
              <h1 className="text-2xl md:text-4xl font-heading font-bold text-white mb-3">
                {categoryName}黄游下载
              </h1>
              <p className="text-sm md:text-base text-white/80 max-w-xl mx-auto">
                {categoryDesc}
              </p>
            </div>
          </section>

          {/* Breadcrumb */}
          <div className="max-w-7xl mx-auto px-4 md:px-6 py-4">
            <Breadcrumb
              items={[
                { label: '首页', href: '/' },
                { label: categoryName },
              ]}
            />
          </div>

          {/* Category Tabs */}
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="flex flex-wrap gap-2 pb-6 border-b border-background-200/60">
              {categories.map((cat) => (
                <Link
                  key={cat.slug}
                  to={`/category/${cat.slug}`}
                  onClick={() => setActiveTab(cat.slug)}
                  className={`px-4 py-2 text-sm font-medium rounded-full transition-all duration-200 whitespace-nowrap cursor-pointer ${
                    activeTab === cat.slug
                      ? 'bg-primary-500 text-background-50'
                      : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                  }`}
                >
                  {cat.name}
                </Link>
              ))}
            </div>
          </div>

          {/* Game Grid */}
          <section className="py-8 md:py-10">
            <div className="max-w-7xl mx-auto px-4 md:px-6">
              <div className="flex items-center justify-between mb-6">
                <p className="text-sm text-foreground-600">
                  共 <strong className="text-foreground-900">{filteredGames.length}</strong> 款游戏
                </p>
              </div>

              {filteredGames.length === 0 ? (
                <div className="text-center py-20">
                  <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-100">
                    <i className="ri-inbox-line text-2xl text-foreground-400"></i>
                  </div>
                  <p className="text-foreground-500">暂无该分类的游戏资源</p>
                </div>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                  {filteredGames.map((game) => (
                    <Link
                      key={game.id}
                      to={`/category/${game.category}`}
                      className="group bg-background-50 rounded-lg border border-background-200/70 overflow-hidden hover:border-primary-300 transition-all duration-200 cursor-pointer"
                    >
                      <div className="relative w-full h-52 overflow-hidden">
                        <img
                          src={gameCardImages[game.imageSeq] || gameCardImages['game-shadow-legend']}
                          alt={game.title}
                          className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-400"
                        />
                        <div className="absolute top-3 left-3 flex items-center gap-2">
                          <span className="px-2 py-0.5 rounded-full bg-primary-500/90 text-background-50 text-xs font-medium">
                            {game.rating}
                          </span>
                        </div>
                        <div className="absolute top-3 right-3">
                          <span className="px-2 py-0.5 rounded-full bg-black/60 text-white text-xs">
                            {game.platform}
                          </span>
                        </div>
                      </div>
                      <div className="p-4">
                        <h3 className="font-heading text-lg font-semibold text-foreground-900 group-hover:text-primary-600 transition-colors duration-200 line-clamp-1">
                          {game.title}
                        </h3>
                        <p className="text-xs text-foreground-500 mt-2 line-clamp-2 leading-relaxed">
                          {game.description}
                        </p>
                        <div className="flex flex-wrap gap-1.5 mt-3">
                          {game.tags.map((tag) => (
                            <span
                              key={tag}
                              className="px-2 py-0.5 text-xs rounded-full bg-secondary-100 text-secondary-700"
                            >
                              {tag}
                            </span>
                          ))}
                        </div>
                        <div className="flex items-center justify-between mt-3 pt-3 border-t border-background-200/60">
                          <div className="flex items-center gap-4">
                            <span className="text-xs text-foreground-500 flex items-center gap-1">
                              <span className="w-3 h-3 flex items-center justify-center"><i className="ri-download-line text-xs"></i></span>
                              {game.downloads}
                            </span>
                            <span className="text-xs text-foreground-500">{game.size}</span>
                          </div>
                          <span className="text-xs text-primary-600 font-medium group-hover:translate-x-0.5 transition-transform duration-200 whitespace-nowrap">
                            查看详情
                          </span>
                        </div>
                      </div>
                    </Link>
                  ))}
                </div>
              )}
            </div>
          </section>
        </main>

        <Footer />

        <script type="application/ld+json">{JSON.stringify(categorySchema)}</script>
      </div>
    </>
  );
}